import { put, select, takeLatest } from "redux-saga/effects";
import { autoLinkEmail } from "../../lib/services";
import flowStages from "../../flowStages";
import actiontypes from "../actions/actiontypes";
import {
    handleError,
    loginSetFlowStageWatcher,
    setEmailAutoLinkFailure,
    handleEmailAutoLinkResult,
    setFetching,
} from "../actions/loginactioncreators";
import getAuthToken from "../helpers/getAuthToken";
import getLocale from "../helpers/getLocale";
import getEmailAutolinkPayload from "../helpers/getEmailAutolinkPayload";
import { navigateToAuthorize, navigateToDashboard } from "../helpers/navigators";
import getClientId from "../helpers/getClientId";

const EMAIL_AUTOLINK_COMPLETE_FLOW_PARAMETER = "EMAIL_AUTOLINK_COMPLETE";

export function* autoLinkEmailAsync(_action) {
    try {
        const payload = yield select(getEmailAutolinkPayload);
        const authToken = yield select(getAuthToken);
        const locale = yield select(getLocale);
        const clientId = yield select(getClientId);

        if (!authToken) {
            throw new Error("Missing auth token for email auto-link request");
        }
        const options = {
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Accept-Language": locale,
            },
        };

        const { data: autoLinkResponse } = yield autoLinkEmail(payload, options);
        yield put(handleEmailAutoLinkResult(autoLinkResponse.users.map(user => {
            user.clientId ??= clientId; // Ensure clientId is included in user data for later matching, even if backend doesn't return it
            user.key = `${user.username}-${user.accountNumber}-${user.clientId}`; // Generate a unique key for the user item
            return user;
        })));
        if (autoLinkResponse.flowStage === "EMAIL_AUTOLINK_FAILED") {
            yield put(setEmailAutoLinkFailure());
            yield put(handleError(autoLinkResponse));
            yield put(loginSetFlowStageWatcher({ flowStage: flowStages.EMAIL_AUTO_LINK }));
        } else if (Array.isArray(autoLinkResponse.users) && autoLinkResponse.users.length > 0) {
            yield put(loginSetFlowStageWatcher({ flowStage: flowStages.EMAIL_AUTO_LINK }));
        } else if (autoLinkResponse.flowStage === "EMAIL_AUTOLINK_COMPLETE" || autoLinkResponse.flowStage === "EMAIL_AUTOLINK_NOTCONFIGURED") {
            yield put(loginSetFlowStageWatcher({ flowStage: flowStages.EMAIL_AUTO_LINK_NO_ACTIONS }));
        } else {
            navigateToAuthorize();
        }
    } catch (error) {
        yield put(setEmailAutoLinkFailure());
        yield put(handleError(error));
    }
}

export function* completeEmailAutoLinkAsync(action) {
    try {
        const authToken = yield select(getAuthToken);
        const locale = yield select(getLocale);
        const clientId = yield select(getClientId);

        if (!authToken) {
            throw new Error("Missing auth token for email auto-link completion request");
        }

        const options = {
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Accept-Language": locale,
            },
        };

        const payload = {
            client_id: clientId,
            flowParameter: EMAIL_AUTOLINK_COMPLETE_FLOW_PARAMETER,
        };
        yield put(setFetching(true));

        const { data } = yield autoLinkEmail(payload, options);
        if (data?.flowStage !== "EMAIL_AUTOLINK_COMPLETE") {
            throw new Error("Unexpected flow stage returned from email auto-link completion request");
        }

        if(action.type === actiontypes.EMAIL_AUTO_LINK_SKIP) {
            navigateToDashboard();
            return;
        }

        yield put(setFetching(false));
        yield put(loginSetFlowStageWatcher({ flowStage: flowStages.AUTHENTICATED }));

        if (typeof action?.payload?.onSuccess === "function") {
            yield action.payload.onSuccess();
        }
    } catch (error) {
        yield put(setEmailAutoLinkFailure());
        yield put(handleError(error));
    }
}

export function* watchEmailAutoLinkAsync() {
    yield takeLatest(actiontypes.EMAIL_AUTO_LINK, autoLinkEmailAsync);
    yield takeLatest([actiontypes.EMAIL_AUTO_LINK_COMPLETE,actiontypes.EMAIL_AUTO_LINK_SKIP], completeEmailAutoLinkAsync);
}
